import React, { useEffect, useState, useRef } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../../components/UI/button";
import { LoaderVectors } from "../../../constants/icons";
import { useSystem } from "../../../hooks/use-system";
import callAPI from "../../../api";
import { defaultLanguage } from "../../../constants/languages";
import { Overlay, Tooltip } from "react-bootstrap";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/UI/form";
import { Input } from "../../../components/UI/input";
import {
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import { cleanMessage, handleApiReturn } from "../../../helpers/logic";
import InputMask from "react-input-mask";

// Define the shake animation
const shakeAnimation = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); box-shadow: 0 0 0 rgba(220, 38, 38, 0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-7px); box-shadow: 0 0 8px rgba(220, 38, 38, 0.5); }
    20%, 40%, 60%, 80% { transform: translateX(7px); box-shadow: 0 0 10px rgba(220, 38, 38, 0.7); }
  }
  .animate-[shake_0.8s_ease-in-out] {
    animation: shake 1s ease-in-out;
  }
`;

const FormSchema = z.object({
  phonenumber: z
    .string()
    .min(8, { message: "" })
    .refine((value) => isValidPhoneNumber(value), {
      message: "",
    }),
});
export function InputForm({
  popUpText,
  config,
  language,
  setStep,
  handleVerificationRequest,
  handleCarrierCode,
  handleSmsInfo,
  isLandscape,
}) {
  const [countrySelected, setCountrySelected] = useState("");
  const [agreed, setAgreed] = useState(
    config.terms_checkbox_switch ? config.default_terms_switch_status : true
  );
  const ref = useRef(null);
  const [shown, showPopover] = useState(false);
  const target = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [showInputAnimation, setShowInputAnimation] = useState(false);
  const system = useSystem();
  const form = useForm({
    resolver: zodResolver(FormSchema),
    mode: "onChange",
    defaultValues: {
      phonenumber: countrySelected,
    },
  });

  const isTesting = config.is_testing;

  // Set up animation after 3 seconds if user hasn't started typing
  useEffect(() => {
    const animationTimer = setTimeout(() => {
      if (!hasStartedTyping) {
        setShowInputAnimation(true);
        // Reset animation after it plays
        setTimeout(() => {
          setShowInputAnimation(false);
        }, 1200); // Increased duration to match animation
      }
    }, 3000);

    return () => clearTimeout(animationTimer);
  }, [hasStartedTyping]);

  useEffect(() => {
    if (config.country_code) {
      const country = getCountryCallingCode(config.country_code?.toUpperCase());
      setCountrySelected(country);
      form.setValue("phonenumber", `+${country}`);
    }
  }, [config.country_code, form]);

  var _supportsLocalStorage =
    !!window.localStorage &&
    typeof localStorage.getItem === "function" &&
    typeof localStorage.setItem === "function" &&
    typeof localStorage.removeItem === "function";

  function onSubmit(data) {
    const phone = form.getValues("phonenumber").replace(/\s/g, '');
    const isPhoneValid = isValidPhoneNumber(phone);
    if (!isPhoneValid) {
      setError("Please enter a valid number");
      return;
    }
    if (loading) return;

    setLoading(true);

    if (_supportsLocalStorage) {
      window.localStorage.setItem("phone", phone);
    }

    if (isTesting) {
      if (config.flow === "PIN_FLOW") {
        setStep((prev) => prev + 1);
      } else if (config.flow === "CLICK_2_SMS_REDIRECT") {
        if (typeof handleSmsInfo === "function") {
          handleSmsInfo(config.short_code, config.keyword);
        }
        setTimeout(() => {
          setStep((prev) => prev + 1);
        }, 3000);
      } else if (["LOOKUP_2_SMS", "PREMIUM_CLICK_2_SMS", "MSISDN_REDIRECT", "INIT_MSISDN_REDIRECT"].includes(config.flow)) {
        if (typeof handleSmsInfo === "function") {
          handleSmsInfo(config.short_code, config.keyword);
        }
        setStep((prev) => prev + 1);
      } else if (config.flow === "PREMIUM_CLICK_2_AUTO_SMS") {
        system.showModal('autoSMS', {
          texts: config.texts[language || defaultLanguage][1], 
          short_code: "12345",
          keyword: "TEST",
          duration: 0.1
        });
      } else {
        setStep((prev) => prev + 1);
      }
      return;
    }

    callAPI(
      {
        phone_number: phone,
        country_code: config.country_code,
        path: window.location.pathname,
        carrier_sub_code: null,
        session_id: localStorage.getItem("nj_sessionId"),
        token: localStorage.getItem("nj_token"),
        supportStorage: _supportsLocalStorage,
        web_click_id: system.webClickId,
        config_id: system.config.public_id,
        qs: window.location.search,
        language: system.language,
        af_token: config.antifraud_token,
      },
      "initSubscription"
    )
      .then(async (data) => {
        setLoading(false);
        if (data.status !== "success") {
          setError(cleanMessage(data.message));
          return;
        }

        if (data.payload.short_code && data.payload.keyword && typeof handleSmsInfo === "function") {
          handleSmsInfo(data.payload.short_code, data.payload.keyword);
        }

        await handleApiReturn(
          config,
          data.payload,
          () => setStep((prev) => prev + 1),
          handleVerificationRequest,
          handleCarrierCode
        );
      })
      .catch((e) => {
        setLoading(false);
      });
  }

  return (
    <Form {...form}>
      {/* Add style tag for custom animation */}
      <style>{shakeAnimation}</style>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={`w-full ${isLandscape ? 'max-w-sm' : 'max-w-md'} flex flex-col justify-center items-center gap-3 ${isLandscape ? 'mt-1' : 'mt-4'} relative`}
      >
        <div className={`${isLandscape ? 'w-full transition-all duration-500 flex flex-col items-center' : ''}`} 
          style={isLandscape ? {
            transform: form.formState.isValid ? 'translateY(-45px)' : 'translateY(0)',
          } : {}}
        >
          {error && (
            <div className={`w-full ${isLandscape ? 'max-w-[300px]' : 'max-w-[275px] sm:max-w-xs'}`}>
              <div className="bg-red-600 text-white text-center py-2 px-6 rounded-lg mb-2">
                {error}
              </div>
            </div>
          )}

          {popUpText && (
            <div
              className={`relative rounded-lg px-3 w-full ${isLandscape ? 'max-w-[300px] transition-all duration-500' : 'max-w-[275px] sm:max-w-xs'} mb-2 ${
                !form.formState.isValid && config.animation_enabled
                  ? "animate-[wiggle_1s_ease-in-out_infinite]"
                  : ""
              }`}
              style={isLandscape ? {
                opacity: form.formState.isValid ? 0 : 1,
                height: form.formState.isValid ? 0 : 'auto',
                overflow: 'hidden',
                marginBottom: form.formState.isValid ? 0 : '0.5rem',
              } : {}}
            >
              <p
                className={`${isLandscape ? 'text-[10px]' : 'text-xs sm:text-sm'} text-center m-0`}
                style={{
                  color: config.text_color || "#000",
                }}
                dangerouslySetInnerHTML={{ __html: popUpText }}
              ></p>
            </div>
          )}

          <div className="w-full flex flex-col pt-1">
            {/* Container with fixed height and relative positioning for warning message */}
            <div className={`w-full ${isLandscape ? 'h-[50px]' : 'h-[60px]'} relative`}>
              {hasStartedTyping && !form.formState.isValid && (
                <div className={`absolute ${isLandscape ? 'bottom-[5px]' : 'bottom-0'} w-full flex justify-center`}>
                  <div className={`w-full ${isLandscape ? 'max-w-[300px]' : 'max-w-[275px] sm:max-w-xs'} mx-auto`}>
                    <div className="bg-red-600 text-white text-center pt-2.5 pb-3.5 px-3 sm:px-4 rounded-lg relative flex items-center justify-center">
                      <p className={`m-0 ${isLandscape ? 'text-[10px]' : 'text-xs sm:text-sm'} break-words hyphens-auto overflow-visible`}>
                        Please enter a valid number
                      </p>
                      <div className="absolute w-4 h-4 bg-red-600 transform rotate-45 left-1/2 -ml-2 bottom-[-0.45rem] z-10"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="phonenumber"
              render={({ field }) => (
                <FormItem className={`w-full ${isLandscape ? 'max-w-[300px]' : 'max-w-[275px] sm:max-w-xs'} mx-auto relative ${isLandscape ? 'mt-1' : 'mt-2'}`}>
                  <FormControl>
                    <div
                      className={`flex w-full rounded-lg border-2 relative z-0 ${
                        form.formState.isValid ? `border-green-500` : `border-blue-600`
                      } ${error ? `border-red-600` : ""} ${
                        showInputAnimation ? "border-red-600 animate-[shake_0.8s_ease-in-out]" : ""
                      } ${isLandscape ? 'h-12' : 'h-14 sm:h-16'} items-center overflow-hidden transition-all`}
                    >
                      {/* Country code prefix */}
                      <div className={`h-full flex justify-center items-center bg-gray-100 text-gray-700 font-medium px-3 ${isLandscape ? 'w-[60px] text-sm' : 'w-[75px] text-base sm:text-lg'}`}>
                        +{countrySelected}
                      </div>
                      
                      {/* Number input field */}
                      <div className="flex-1 h-full flex items-center">
                        <InputMask
                          mask={`${config.phone_mask}`}
                          maskChar="_"
                          value={field.value.replace(new RegExp(`^\\+${countrySelected}\\s*`), '')}
                          onChange={(e) => {
                            const completeValue = `+${countrySelected} ${e.target.value}`;
                            field.onChange(completeValue);
                            if (!hasStartedTyping && e.target.value.trim() !== '') {
                              setHasStartedTyping(true);
                            }
                          }}
                          onBlur={field.onBlur}
                          disabled={loading}
                          alwaysShowMask
                          formatChars={{
                            "#": "[0-9]",
                            "a": "[A-Za-z]",
                            "*": "[A-Za-z0-9]",
                          }}
                        >
                          {(inputProps) => (
                            <Input
                              {...inputProps}
                              onClick={(e) => {
                                if (inputProps.onClick) {
                                  inputProps.onClick(e);
                                }
                                const inputEl = e.target;
                                setTimeout(() => {
                                  const firstEditableChar = inputEl.value.indexOf('_');
                                  if (firstEditableChar !== -1) {
                                    inputEl.setSelectionRange(firstEditableChar, firstEditableChar);
                                  }
                                }, 0);
                              }}
                              className={`h-full w-full pl-4 pr-3 focus:outline-none focus-visible:ring-0 border-none ${isLandscape ? 'text-base' : 'text-lg sm:text-xl'} tracking-widest text-gray-600 flex items-center`}
                            />
                          )}
                        </InputMask>
                      </div>
                      
                      {form.formState.isValid && !error && (
                        <div className="h-full flex items-center justify-center pr-3">
                          <svg width={isLandscape ? "20" : "24"} height={isLandscape ? "20" : "24"} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="10" fill="#47C98D"/>
                            <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {config.terms_checkbox_switch && (
          <div
            className={`flex items-start w-full ${isLandscape ? 'max-w-[300px]' : 'max-w-[275px] sm:max-w-xs'} gap-2 ${isLandscape ? 'mb-1' : 'mb-2'} ${isLandscape ? 'transition-all duration-500' : ''} mx-auto`}
            style={{
              color: config.text_color,
              ...(isLandscape ? {
                transform: form.formState.isValid ? 'translateY(-55px)' : 'translateY(0px)',
                zIndex: 10
              } : {})
            }}
            ref={ref}
          >
            <Overlay
              show={shown}
              target={target}
              placement="bottom"
              container={ref}
              containerPadding={20}
            >
              <Tooltip placement={"bottom"}>Select this checkbox!</Tooltip>
            </Overlay>
            <input
              ref={target}
              id="terms-check"
              onChange={() => setAgreed((prev) => !prev)}
              type={"checkbox"}
              checked={agreed}
              className="mt-0.5 flex-shrink-0"
            />
            <div className="text-left flex-1">
              <label htmlFor="terms-check" className={`${isLandscape ? 'text-[10px]' : 'text-xs'} block`}>
                {config.translations?.[language || defaultLanguage]?.terms_agreement || ""}
              </label>
              {config.append_terms_link_to_consent && (
                <a
                  style={{ color: config.titles_color }}
                  href={config.terms_url}
                  target="_blank"
                  rel="noreferrer"
                  className={`${isLandscape ? 'text-[10px]' : 'text-xs'} inline ml-1`}
                >
                  {config.terms_text}
                </a>
              )}
            </div>
          </div>
        )}

        <div className={`w-full flex justify-center ${isLandscape ? 'transition-all duration-500' : ''}`} 
          style={isLandscape ? {
            opacity: (form.formState.isValid && agreed) ? 1 : 0,
            transform: (form.formState.isValid && agreed) ? 'translateY(-55px)' : 'translateY(20px)',
            pointerEvents: (form.formState.isValid && agreed) ? 'auto' : 'none',
            visibility: (form.formState.isValid && agreed) ? 'visible' : 'hidden',
            marginTop: '0',
          } : {}}
        >
          <Button
            type="submit"
            className={`${isLandscape 
              ? `min-h-[44px] px-5 py-3 w-full max-w-[300px] text-sm mt-2 rounded-lg text-white ${loading ? "cursor-not-allowed" : ""}`
              : `flex flex-row gap-2 min-h-[48px] sm:min-h-[56px] px-5 py-3 w-full max-w-[275px] sm:max-w-xs ${
                loading ? "cursor-not-allowed" : "cursor-pointer"
              } text-white bg-blue-600 hover:bg-blue-700 font-semibold text-base sm:text-lg rounded-lg ${
                !error && form.formState.isValid && agreed && config.animation_enabled
                  ? "animate-[pulse_1.5s_ease-in-out_infinite]"
                  : ""
              }`
            }`}
            style={{
              backgroundColor: config.button_primary_color || "#2563eb",
              color: config.button_text_color || "#fff",
              fontSize: isLandscape ? config.button_font_size : undefined,
              ...(isLandscape ? {} : {
                height: "auto",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                hyphens: "auto",
              })
            }}
            disabled={loading || !form.formState.isValid || !agreed}
          >
            {loading ? (
              <>
                <span className="animate-spin flex-shrink-0">
                  <LoaderVectors />
                </span>
                <div className="flex flex-row justify-end items-center">
                  <p className="m-0" style={{ fontSize: config.button_font_size }}>
                    {config.texts?.[language || defaultLanguage]?.[0]?.button_loading_text || "Processing"}
                  </p>
                  <span className="animate-bounce">.</span>
                  <span className="animate-bounce delay-200">.</span>
                  <span className="animate-bounce delay-300">.</span>
                </div>
              </>
            ) : (
              <span className="text-center w-full break-words">{config.texts?.[language || defaultLanguage]?.[0]?.button_text || "Continue"}</span>
            )}
          </Button>
        </div>
        <div className="flex justify-center items-center w-full">
          <span
            className={`${isLandscape ? 'text-[10px]' : 'text-sm'}`}
            style={{
              color: config.text_color || "#000",
            }}
          >
            {config.texts?.[language || defaultLanguage]?.[0]?.button_caption ||
              ""}
          </span>
        </div>
      </form>
    </Form>
  );
}